// Audio sources: decoded files and built-in test tones. Everything is 44.1 kHz.

export const SR = 44100;

export interface AudioSource {
  name: string;
  sampleRate: number;
  /** One or two channels, float in [-1, 1]. */
  channels: Float32Array[];
}

export function duration(src: AudioSource): number {
  return src.channels[0].length / src.sampleRate;
}

/** Decode any browser-supported file (wav/mp3/flac/ogg/m4a...) to 44.1 kHz. */
export async function decodeFile(file: File): Promise<AudioSource> {
  const buf = await file.arrayBuffer();
  const ctx = new OfflineAudioContext(2, 1, SR);
  const audio = await ctx.decodeAudioData(buf);
  const channels = [];
  for (let c = 0; c < Math.min(2, audio.numberOfChannels); c++) channels.push(audio.getChannelData(c).slice());
  return { name: file.name.replace(/\.[^.]+$/, ""), sampleRate: audio.sampleRate, channels };
}

export type ToneId = "sine1k" | "sweep" | "stereoLR" | "chord" | "voice";

export const TONES: Record<ToneId, string> = {
  sine1k: "1 kHz sine (pitch check)",
  sweep: "Log sweep 100 Hz → 10 kHz (aliasing check)",
  stereoLR: "L 440 Hz / R 660 Hz (channel check)",
  chord: "A-major arpeggio (musical)",
  voice: "Formant 'vowel' buzz (speech-like)",
};

export function makeTone(id: ToneId, seconds = 10): AudioSource {
  const n = Math.round(seconds * SR);
  const L = new Float32Array(n), R = new Float32Array(n);
  const tau = 2 * Math.PI;
  switch (id) {
    case "sine1k":
      for (let i = 0; i < n; i++) L[i] = R[i] = 0.8 * Math.sin((tau * 1000 * i) / SR);
      break;
    case "sweep": {
      const f0 = 100, f1 = 10000, k = Math.log(f1 / f0);
      for (let i = 0; i < n; i++) L[i] = R[i] = 0.8 * Math.sin(((tau * f0 * seconds) / k) * (Math.exp((k * i) / n) - 1));
      break;
    }
    case "stereoLR":
      for (let i = 0; i < n; i++) {
        L[i] = 0.8 * Math.sin((tau * 440 * i) / SR);
        R[i] = 0.8 * Math.sin((tau * 660 * i) / SR);
      }
      break;
    case "chord": {
      const notes = [220, 277.18, 329.63, 440, 329.63, 277.18];
      const noteLen = Math.round(0.25 * SR);
      for (let i = 0; i < n; i++) {
        const note = Math.floor(i / noteLen), t = (i % noteLen) / SR;
        const f = notes[note % notes.length];
        const env = Math.exp(-t * 6);
        let v = 0;
        for (let h = 1; h <= 6; h++) v += Math.sin(tau * f * h * (i / SR)) / h;
        L[i] = R[i] = 0.45 * env * v;
      }
      break;
    }
    case "voice": {
      // Glottal-ish buzz at 120 Hz shaped by three formant resonances (an "ah").
      const formants = [[730, 80], [1090, 90], [2440, 120]];
      const state = formants.map(() => [0, 0]);
      for (let i = 0; i < n; i++) {
        const ph = ((i * (120 + 8 * Math.sin((tau * 0.7 * i) / SR))) / SR) % 1;
        const src = ph < 0.1 ? 1 - ph * 10 : 0;
        let v = 0;
        formants.forEach(([f, bw], k) => {
          const r = Math.exp((-Math.PI * bw) / SR), c = 2 * r * Math.cos((tau * f) / SR);
          const y = src + c * state[k][0] - r * r * state[k][1];
          state[k][1] = state[k][0];
          state[k][0] = y;
          v += y / (k + 1);
        });
        L[i] = R[i] = v;
      }
      let m = 0;
      for (let i = 0; i < n; i++) m = Math.max(m, Math.abs(L[i]));
      for (let i = 0; i < n; i++) L[i] = R[i] = (0.8 * L[i]) / m;
      break;
    }
  }
  return { name: id, sampleRate: SR, channels: [L, R] };
}

/** First `seconds` of a source (or the whole thing if shorter / seconds <= 0). */
export function clip(src: AudioSource, seconds: number): AudioSource {
  if (!(seconds > 0)) return src;
  const n = Math.min(src.channels[0].length, Math.round(seconds * src.sampleRate));
  return { ...src, channels: src.channels.map((c) => c.subarray(0, n)) };
}
